import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import type { DataSource } from "@/lib/api";

const KEY = "llm-studio-data-source";

export const SOURCE_OPTIONS: { value: DataSource; label: string }[] = [
  { value: "real", label: "Real" },
  { value: "fabricated", label: "Fabricated" },
];

interface DataSourceValue {
  source: DataSource;
  setSource: (v: DataSource) => void;
}

const DataSourceContext = createContext<DataSourceValue>({ source: "real", setSource: () => {} });

function read(): DataSource {
  try {
    return localStorage.getItem(KEY) === "fabricated" ? "fabricated" : "real";
  } catch {
    return "real";
  }
}

export function DataSourceProvider({ children }: { children: ReactNode }) {
  const [source, setState] = useState<DataSource>(read);
  const setSource = useCallback((v: DataSource) => {
    setState(v);
    try {
      localStorage.setItem(KEY, v);
    } catch {
      /* storage unavailable */
    }
  }, []);
  const value = useMemo(() => ({ source, setSource }), [source, setSource]);
  return <DataSourceContext.Provider value={value}>{children}</DataSourceContext.Provider>;
}

export function useDataSource() {
  return useContext(DataSourceContext);
}
